/**
 * Follow-up Workflow
 *
 * Handles follow-up workflow data fetching for specific institutions
 * Extracted from workflow-data.service.ts
 */

import {
  apiGet,
  isSuccessResponse,
  extractErrorMessage
} from '@/lib/utils/api-helpers';
import { transformInstitution } from '../transformers';
import { getLawFirmData } from '../services/law-firm.service';
import { getSignatories } from '../services/signatory.service';
import { extractDocuments, extractInventory } from './base.workflow';
import type { WorkflowData } from '../workflow-types';
import { getUserDisplayName } from '@/lib/utils/permissions';

/**
 * Fetch data needed for follow-up workflow
 * Similar to notification but for a specific institution
 */
export async function getFollowUpWorkflowData(
  matterId: number,
  institutionId: number
): Promise<WorkflowData> {
  try {
    console.log('Fetching follow-up workflow data:', {
      matterId,
      institutionId
    });

    // Fetch all data in parallel
    // Note: All endpoints use /api/proxy/ to ensure consistent auth via NextAuth session
    const [
      matterResponse,
      institutionResponse,
      documentsResponse,
      inventoryResponse
    ] = await Promise.all([
      apiGet(`/api/proxy/matters/${matterId}`),
      apiGet(`/api/proxy/matters/${matterId}/institutions/${institutionId}`),
      apiGet(`/api/proxy/matters/${matterId}/files`),
      apiGet(`/api/proxy/matters/${matterId}/inventory-items`)
    ]);

    if (!isSuccessResponse(matterResponse)) {
      throw new Error(extractErrorMessage(matterResponse));
    }

    const matter = matterResponse.data as {
      id: number;
      deceasedName?: string;
      dateOfDeath?: string;
      caseReference?: string;
      lawFirmInternalReference?: string;
      actingSolicitor?: {
        id: number;
        firstName: string | null;
        lastName: string | null;
        email: string;
        hasSignature?: boolean;
      } | null;
    };

    // Get specific institution data
    let institution: unknown = null;
    if (isSuccessResponse(institutionResponse)) {
      institution = institutionResponse.data;
    }

    // Extract documents and inventory
    const documents = extractDocuments(documentsResponse, 'Follow-up');
    const inventory = extractInventory(inventoryResponse);

    // Get law firm data and signatories in parallel
    const [lawFirm, signatories] = await Promise.all([
      getLawFirmData(),
      getSignatories()
    ]);

    // Transform institution for workflow
    const workflowInstitution = institution
      ? transformInstitution(institution, inventory, [])
      : null;

    return {
      matter: {
        id: matter.id,
        deceasedName: matter.deceasedName || '',
        dateOfDeath: matter.dateOfDeath || '',
        referenceNumber: matter.caseReference || '',
        lawFirmInternalReference: matter.lawFirmInternalReference || '',
        actingSolicitor: matter.actingSolicitor
          ? {
              id: matter.actingSolicitor.id,
              name: getUserDisplayName(matter.actingSolicitor),
              email: matter.actingSolicitor.email,
              hasSignature: matter.actingSolicitor.hasSignature || false
            }
          : null
      },
      institutions: workflowInstitution ? [workflowInstitution] : [],
      availableDocuments: documents,
      availableSignatories: signatories,
      lawFirm
    };
  } catch (error) {
    console.error('Failed to fetch follow-up workflow data:', error);
    throw error;
  }
}
